import { PrescricaoType } from '_metronic/helpers/enums'
import type { CustomOption } from '../_model'
import type { ExameView, FormulaView, MedicamentoView, OrientacaoView } from '../_model'
import { CategoriaItem, ItemCatalogo } from './itens'

export const medicamentoDoCatalogo = (medicamento: MedicamentoView): ItemCatalogo => ({
  id: medicamento.id,
  categoria: PrescricaoType.Medicamento,
  nome: medicamento.nome,
  concentracao: medicamento.concentracao,
  apresentacao: medicamento.apresentacao,
  classe: medicamento.classe,
  tarja: medicamento.tarja,
  receituario: medicamento.receituario,
  controleEspecial: medicamento.controleEspecial,
  lme: medicamento.lme,
  codigo: medicamento.codigo,
  criador: medicamento.criador,
  ativo: medicamento.ativo,
})

export const formulaDoCatalogo = (formula: FormulaView): ItemCatalogo => ({
  id: formula.id,
  categoria: PrescricaoType.Formula,
  nome: formula.nome ?? '',
  composicao: formula.descricao,
  criador: formula.criador,
})

export const exameDoCatalogo = (exame: ExameView): ItemCatalogo => ({
  id: exame.id,
  categoria: PrescricaoType.Exame,
  nome: exame.nome ?? '',
  tipoExame: exame.tipo,
  apac: exame.apac,
  codigo: exame.codigo,
  criador: exame.criador,
  ativo: exame.ativo,
})

export const orientacaoDoCatalogo = (orientacao: OrientacaoView): ItemCatalogo => ({
  id: orientacao.id,
  categoria: PrescricaoType.Orientacoes,
  nome: orientacao.nome ?? '',
  textoPadrao: orientacao.descricao,
  criador: orientacao.criador,
  ativo: orientacao.ativa,
})

/**
 * Filtro da busca no DSL do backend (`campo~*valor*`, `'` separa as alternativas com OU).
 * Medicamento também busca pelo princípio ativo. A vírgula é o separador do DSL, então sai.
 */
export const filtroDeBusca = (categoria: CategoriaItem, termo: string) => {
  const limpo = termo.replace(/,/g, ' ').trim()
  if (!limpo) return ''
  const filtros = [`nome~*${limpo}*`]
  if (categoria === PrescricaoType.Medicamento) filtros.push(`substancia~*${limpo}*`)
  return filtros.join(",'")
}

/**
 * Ponte com os modais de cadastro (Medicamento/Fórmula/Exame/OrientacaoModal), que falam
 * `CustomOption`: `descricao` é a composição na fórmula e o texto na orientação.
 */
export const opcaoDoCatalogo = (catalogo: ItemCatalogo): NonNullable<CustomOption> => ({
  value: catalogo.id ?? null,
  label: catalogo.nome,
  concentracao: catalogo.concentracao,
  apresentacao: catalogo.apresentacao,
  classe: catalogo.classe,
  tarja: catalogo.tarja,
  receituario: catalogo.receituario,
  controleEspecial: catalogo.controleEspecial,
  lme: catalogo.lme,
  apac: catalogo.apac,
  codigo: catalogo.codigo,
  tipo: catalogo.tipoExame,
  descricao:
    catalogo.categoria === PrescricaoType.Formula
      ? catalogo.composicao
      : catalogo.categoria === PrescricaoType.Orientacoes
      ? catalogo.textoPadrao
      : undefined,
  criador: catalogo.criador,
  ativo: catalogo.ativo,
})

export const catalogoDaOpcao = (
  opcao: NonNullable<CustomOption>,
  categoria: CategoriaItem,
  anterior?: ItemCatalogo
): ItemCatalogo => ({
  ...anterior,
  // O modal de orientação sempre devolve `value` nulo: editar o texto não tira ela do catálogo.
  id: opcao.value
    ? Number(opcao.value)
    : categoria === PrescricaoType.Orientacoes
    ? anterior?.id
    : undefined,
  categoria,
  nome: opcao.label,
  concentracao: opcao.concentracao,
  apresentacao: opcao.apresentacao,
  classe: opcao.classe ?? anterior?.classe,
  tarja: opcao.tarja,
  receituario: opcao.receituario,
  tipoExame: opcao.tipo ?? anterior?.tipoExame,
  apac: opcao.apac ?? anterior?.apac,
  codigo: opcao.codigo ?? anterior?.codigo,
  composicao: categoria === PrescricaoType.Formula ? opcao.descricao : undefined,
  textoPadrao: categoria === PrescricaoType.Orientacoes ? opcao.descricao : undefined,
})

/** O médico só inativa o que ele mesmo cadastrou, e o backend não inativa fórmula. */
export const podeInativar = (catalogo: ItemCatalogo, pessoaId?: number) =>
  catalogo.categoria !== PrescricaoType.Formula &&
  !!catalogo.id &&
  !!catalogo.criador &&
  catalogo.criador.id === pessoaId
